import { ApplicationRef, EventEmitter, Injectable } from '@angular/core';
import Venue from '../../models/Venue';
import Order from '../../models/Order';
import Payment from '../../models/Payment';
import Customer from '../../models/Customer';
import Address from '../../models/Address';
import { Api } from '../../api/api';
import { PreorderType } from '../../enums/PreorderType';
import { OrderType } from '../../enums/OrderType';
import Preorder from '../../models/Preorder';
import OptionGroup from '../../models/OptionGroup';
import Article from '../../models/Article';
import { OrderUtils } from '../../utils/order-utils';
import { TimeUtils } from '../../utils/time-utils';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { AuthLoginResponse } from '../../models/AuthLoginResponse';
import { BehaviorSubject, Subject, Subscription, timer } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../environments/environment';
import { CustomerAuth } from '../../models/CustomerAuth';
import { PaymentModalComponent, PaymentModalResult } from '../../components/payment-modal/payment-modal.component';
import { DisplayIdentifier } from '../../enums/DisplayIdentifier';
import {
	axiosErrorToMessage,
	getSlots,
	isActive,
	numberComparator,
	numberD,
	paymentFromOrder,
	paymentFromOrders,
	sleep,
	venueAcceptsOrders,
} from '../../utils/utils';
import { AnalyticsService } from '../analytics/analytics.service';
import { DisplayMode } from '../../enums/DisplayMode';
import Table from 'src/smoothr-web-app-core/models/Table';
import { delay, pairwise } from 'rxjs/operators';
import { OrderStatus } from 'src/smoothr-web-app-core/enums/OrderStatus';
import { NavigationService } from 'src/app/services/navigation.service';
import { PaymentMethod } from 'src/smoothr-web-app-core/enums/PaymentMethod';
import { CheckoutIntegrationType } from 'src/smoothr-web-app-core/enums/CheckoutIntegrationType';
import CheckoutRecommendation from 'src/smoothr-web-app-core/models/CheckoutRecommendation';
import ArticleGroup from 'src/smoothr-web-app-core/models/ArticleGroup';
import { Company } from 'src/smoothr-web-app-core/models/CompanyCode';
import { paymentBody } from 'src/smoothr-web-app-core/models/Converter';
import PromoCode from 'src/smoothr-web-app-core/models/PromoCode';
import { PromoCodeType } from 'src/smoothr-web-app-core/models/PromoCodeType';
import { PaymentCancelledModalComponent } from '../../../app/components/payment-cancelled-modal/payment-cancelled-modal.component';
import { PaymentStatus } from '../../models/PaymentStatus';
import { CheckingPaymentModalComponent } from '../../../app/components/checking-payment-modal/checking-payment-modal.component';
import * as Sentry from '@sentry/angular';
import { ChangedPrivacyPolicyModalComponent } from '../../../app/components/changed-privacy-policy-modal/changed-privacy-policy-modal.component';
import { BrazeService } from '../../../app/services/braze.service';
import { Router } from '@angular/router';
import { CompressedStorageService } from '../compression';

const localStorageOrderKey = 'repository-service-order';
const localStorageVerifiedOrderKey = 'repository-service-verified-order';
const localStorageCustomerKey = 'repository-service-customer-auth';
const localStoragePaymentKey = 'repository-service-payment';
const localStorageAddressKey = 'repository-service-address';
const localStoragePreviousPreorderKey = 'repository-service-previous-preorder';
const localStorageAuthLoginKey = 'repository-service-auth-login';
const localStorageFavouriteKey = 'repository-service-favourite';
const localStorageUpdatedPrivacyPolicyShowed = 'repository-service-updated-privacy-policy-showed';

@Injectable({
	providedIn: 'root',
})
export class RepositoryService {
	venue = new EventEmitter<Venue>();
	allVenues$: BehaviorSubject<Venue[]> = new BehaviorSubject<Venue[]>([]);
	address = new EventEmitter<Address>();
	private order = new Subject<Order>();
	obserableOrder: Subject<Order> = this.order;
	verifiedOrder = new EventEmitter<Order>();
	payment = new EventEmitter<Payment>();
	customerAuth = new EventEmitter<CustomerAuth>();
	previousPreorder = new EventEmitter<Preorder>();
	authLogin = new EventEmitter<AuthLoginResponse>();
	orders = new BehaviorSubject<Order[]>([]);
	favorite = new EventEmitter<string[]>();
	lastSuccessfullScan = new BehaviorSubject<number>(null);
	recommendationList$ = new BehaviorSubject<CheckoutRecommendation[]>([]);
	companyCode$ = new BehaviorSubject<Company>(null);
	sanifairSub: Subscription;
	_venue: Venue;
	_address: Address;
	_order: Order;
	_verifiedOrder: Order;
	_payment: Payment;
	_customerAuth: CustomerAuth;
	_previousPreorder: Preorder;
	_authLogin: AuthLoginResponse;
	_favorite: string[];
	promoCodeFree$ = new BehaviorSubject<PromoCode>(null);

	private apiTokenRefreshSubscription: Subscription;

	private venuePromise: Promise<Venue> = null;
	private venuePromiseRunning: string = null;

	constructor(
		private appRef: ApplicationRef,
		private analytics: AnalyticsService,
		private snackBarCtrl: MatSnackBar,
		private translate: TranslateService,
		private navService: NavigationService,
		private modalCtrl: ModalController,
		private brazeService: BrazeService,
		private router: Router,
		private compressedStorageService: CompressedStorageService
	) {
		this._venue = null;
		try {
			this._order = JSON.parse(this.compressedStorageService.getItem(localStorageOrderKey));
		} catch (e) {
			console.error(e);
			this.compressedStorageService.setItem(localStorageOrderKey, null);
		}
		try {
			this._payment = JSON.parse(this.compressedStorageService.getItem(localStoragePaymentKey));
		} catch (e) {
			console.error(e);
			this.compressedStorageService.setItem(localStoragePaymentKey, null);
		}
		try {
			this._customerAuth = JSON.parse(this.compressedStorageService.getItem(localStorageCustomerKey));
			Api.authToken = this._customerAuth?.token;
			Api.refreshToken = this._customerAuth?.refreshToken;
		} catch (e) {
			console.error(e);
			this.compressedStorageService.setItem(localStorageCustomerKey, null);
		}
		try {
			this._address = JSON.parse(this.compressedStorageService.getItem(localStorageAddressKey));
		} catch (e) {
			console.error(e);
			this.compressedStorageService.setItem(localStorageAddressKey, null);
		}
		try {
			this._verifiedOrder = JSON.parse(this.compressedStorageService.getItem(localStorageVerifiedOrderKey));
		} catch (e) {
			console.error(e);
			this.compressedStorageService.setItem(localStorageVerifiedOrderKey, null);
		}
		try {
			this._previousPreorder = JSON.parse(this.compressedStorageService.getItem(localStoragePreviousPreorderKey));
		} catch (e) {
			console.error(e);
			this.compressedStorageService.setItem(localStoragePreviousPreorderKey, null);
		}
		try {
			this._authLogin = JSON.parse(this.compressedStorageService.getItem(localStorageAuthLoginKey));
		} catch (e) {
			console.error(e);
			this.compressedStorageService.setItem(localStorageAuthLoginKey, null);
		}
		try {
			const companyCode = JSON.parse(this.compressedStorageService.getItem('companyCode'));
			if (companyCode) {
				this.companyCode$.next(companyCode);
			}
		} catch (e) {
			console.log('e', e);
			this.companyCode$.next(null);
		}
		try {
			this._favorite = JSON.parse(this.compressedStorageService.getItem(localStorageFavouriteKey));
		} catch (e) {
			console.error(e);
			this.compressedStorageService.setItem(localStorageFavouriteKey, JSON.stringify([]));
		}
		this.order.subscribe(order => {
			if (order) {
				OrderUtils.injectRequiredArticles(this._venue, order, analytics);
				OrderUtils.injectDeliveryFees(this._venue, order);
				if (order.promoCode && order.promoCode.type === PromoCodeType.SPECIAL) {
					OrderUtils.deletedDiscountByDefault(order);
				}

				// if (this._venue) order.orderedArticles = OrderUtils.addPfandArticles(this._venue, order);

				order.orderedArticles = order.orderedArticles.map(articleGroup => {
					articleGroup.groups = articleGroup.groups.sort((a, b) => numberComparator(a.article.sortOrder, b.article.sortOrder));
					return articleGroup;
				});
			}
			this._order = order;

			// this.compressedStorageService.setItem(localStorageOrderKey, JSON.stringify(order));
			this.compressedStorageService.setItem(localStorageOrderKey, JSON.stringify(order));
			Sentry.setContext('order', {
				code: order?.code,
				id: order?._id,
			});
			appRef.tick();
		});
		this.order.pipe(delay(1000), pairwise()).subscribe(([prevOrder, newOrder]) => {
			if (prevOrder && newOrder?.orderedArticles?.length > 0 && environment.hasRecommendations) {
				if (newOrder?.orderedArticles?.length != prevOrder?.orderedArticles?.length) {
					this.calculateRecommendation(newOrder);
				} else {
					if (!this.checkIfOrdersTheSame(prevOrder, newOrder)) {
						this.calculateRecommendation(newOrder);
					}
				}
			}
		});

		this.verifiedOrder.subscribe(verifiedOrder => {
			this.stopTimer();
			if (verifiedOrder && verifiedOrder?.status === OrderStatus.CREATED) {
				this.startTimer();
			}

			this._verifiedOrder = verifiedOrder;
			Sentry.setContext('verifiedOrder', {
				code: verifiedOrder?.code,
				id: verifiedOrder?._id,
			});
			this.compressedStorageService.setItem(localStorageVerifiedOrderKey, JSON.stringify(verifiedOrder));
			appRef.tick();
		});
		this.address.subscribe(address => {
			this._address = address;
			this.compressedStorageService.setItem(localStorageAddressKey, JSON.stringify(address));
			appRef.tick();
		});
		this.payment.subscribe(payment => {
			this._payment = payment;
			this.compressedStorageService.setItem(localStoragePaymentKey, JSON.stringify(payment));
			appRef.tick();
		});
		this.customerAuth.subscribe(async customerAuth => {
			Sentry.setContext('customer', {
				id: customerAuth?.customer?._id,
				email: customerAuth?.customer?.email,
			});

			if (!customerAuth) {
				console.log('customer logged out');

				// // customer was online and now was logged out.
				// if (this._customerAuth && this._customerAuth.customer && !this._customerAuth.customer.isAnonymous) {
				// 	this.snackBarCtrl.open(this.translate.instant('repository_service.login_timed_out'), null, {
				// 		duration: 3000,
				// 	});
				// }
				try {
					const response = (await Api.signInAnonymous(customerAuth?.customer?.uid)).data;
					this.customerAuth.emit(response);
				} catch (e) {
					console.error(e);
					this.snackBarCtrl.open(axiosErrorToMessage(this.translate, e), null, {
						duration: 2000,
					});
					this.compressedStorageService.setItem(localStorageCustomerKey, JSON.stringify(null));
				}
				return;
			}

			if (!customerAuth.customer.country) {
				customerAuth.customer.country = environment.countryList[0];
			} else {
				customerAuth.customer.country = customerAuth.customer.country.toLowerCase();
			}

			if (customerAuth?.customer?.dndCompany) {
				try {
					customerAuth.customer.dndCompanyObject = (await Api.getCompanyById(customerAuth.customer.dndCompany)).data;
					if (customerAuth.customer.dndCompanyObject.openingHours) {
						customerAuth.customer.dndCompanyObject.openingHours = TimeUtils.sanitizeHours(
							customerAuth.customer.dndCompanyObject.openingHours
						);
						this.companyCode$.next(customerAuth.customer.dndCompanyObject);
					}
				} catch (e) {
					customerAuth.customer.dndCompany = '';
					this.companyCode$.next(null);
				}
			}
			this._customerAuth = customerAuth;
			Api.authToken = this._customerAuth.token;
			Api.refreshToken = this._customerAuth.refreshToken;
			this.compressedStorageService.setItem(localStorageCustomerKey, JSON.stringify(customerAuth));
			if (this._order) {
				this._order.userUid = this._customerAuth.customer.uid;
				this.order.next(this._order);
			}
			appRef.tick();

			const result = (await Api.getCustomerOrders(this.customer.uid)).data; //todo: it is temporary
			if (result.withPayment) {
				this.orders.next(
					result.withPayment.concat(result.withoutPayment).filter(it => it?.preorder?.type !== PreorderType.FOOD_SPOT)
				);
			}

			if (!customerAuth?.customer?.isAnonymous && customerAuth.customer?.name) {
				const updatedPrivacyPolicyShowed =
					JSON.parse(this.compressedStorageService.getItem(localStorageUpdatedPrivacyPolicyShowed)) || {};
				if (
					!updatedPrivacyPolicyShowed?.[customerAuth.customer._id] &&
					(this.router.url.includes('home') || this.router.url.includes('sign-in'))
				) {
					this.brazeService.initUser(customerAuth);

					await ChangedPrivacyPolicyModalComponent.show(this.modalCtrl);
					updatedPrivacyPolicyShowed[customerAuth.customer._id] = true;
					this.compressedStorageService.setItem(
						localStorageUpdatedPrivacyPolicyShowed,
						JSON.stringify(updatedPrivacyPolicyShowed)
					);
				}
			} else if (this.orders.value.length) {
				this.brazeService.initUser(customerAuth);
			} else this.brazeService.truckUserEvents = false;
		});
		this.apiTokenRefreshSubscription = Api.onTokenRefreshed.subscribe(customerAuth => {
			this.customerAuth.emit(customerAuth);
		});
		this.venue.subscribe(venue => {
			this._venue = venue;
			this.order.next(this._order);
			Sentry.setContext('venue', {
				name: venue?.name,
				readableId: venue?.readableId,
			});
			appRef.tick();
		});
		this.previousPreorder.subscribe(preorder => {
			this._previousPreorder = preorder;
			this.compressedStorageService.setItem(localStoragePreviousPreorderKey, JSON.stringify(preorder));
		});
		this.authLogin.subscribe(authLogin => {
			this._authLogin = authLogin;
			this.compressedStorageService.setItem(localStorageAuthLoginKey, JSON.stringify(authLogin));
		});
		this.favorite.subscribe(favourite => {
			this._favorite = favourite;
			this.compressedStorageService.setItem(localStorageFavouriteKey, JSON.stringify(favourite));
		});
		this.companyCode$.subscribe(code => {
			if (code) {
				this.compressedStorageService.setItem('companyCode', JSON.stringify(code));
			}
		});

		this.address.emit(this._address);
		this.order.next(this._order);
		this.verifiedOrder.emit(this._verifiedOrder);
		this.payment.emit(this._payment);
		if (!this._customerAuth) this.customerAuth.emit(this._customerAuth);
		this.authLogin.emit(this._authLogin);
		this.favorite.emit(this._favorite);
		this.reloadCustomer();
	}

	get customer(): Customer {
		return this._customerAuth && this._customerAuth.customer;
	}

	async reloadCustomer(): Promise<Customer> {
		if (!this._customerAuth) {
			return null;
		}
		try {
			const response = await Api.getCustomer();
			this._customerAuth.customer = response.data;
		} catch (e) {
			console.error(e);
		}
		this.customerAuth.emit(this._customerAuth);
		return this._customerAuth.customer;
	}

	resetVenue() {
		this._venue = null;
		this.venue.emit(this._venue);
	}
	onOrderChange(order: Order) {
		// if (order && this._verifiedOrder && !this._verifiedOrder.isPayed) {
		// 	this.invalidateOrder(this._verifiedOrder);
		// 	this.verifiedOrder.emit(null);
		// }

		this.order.next(order);
	}
	async calculateRecommendationForArticle(articleGroup: ArticleGroup, order: Order) {
		try {
			const objectData = {
				venue: order.venue,
				articles: [
					{
						_id: articleGroup.article._id,
						quantity: articleGroup.quantity,
					},
				],
				orderType: OrderType.PREORDER,
				subOrderType: order.preorder.type === PreorderType.TAKE_AWAY ? 'takeAway' : 'inside',
			};
			return (await Api.getCartRecommendations(objectData)).data;
		} catch (e) {
			return [];
		}
	}
	async calculateRecommendation(order: Order) {
		if (order && order?.venue && order?.orderedArticles?.length > 0) {
			try {
				const objectData = {
					venue: order.venue,
					articles: order.orderedArticles.map(it => {
						return { _id: it.article._id, quantity: it.quantity };
					}),
					orderType: OrderType.PREORDER,
					subOrderType: order.preorder.type === PreorderType.TAKE_AWAY ? 'takeAway' : 'inside',
				};
				const result = (await Api.getCartRecommendations(objectData)).data;
				this.recommendationList$.next(result);
			} catch (e) {
				Sentry.captureEvent({
					message: `Error calculating recommendations for ${order.venueName}`,
					extra: {
						order: order._id,
						venue: order.venue,
						venueName: order.venueName,
						error: e,
					},
				});
				this.recommendationList$.next([]);
			}
		} else {
			this.recommendationList$.next([]);
		}
	}
	checkIfOrdersTheSame(newOrder: Order, prevOrder: Order) {
		const mappedPrevOrder = new Map((prevOrder?.orderedArticles ?? []).map(it => [it?.article?._id, it?.quantity]));
		return newOrder.orderedArticles.every(
			it => mappedPrevOrder.has(it.article._id) && mappedPrevOrder.get(it.article._id) === it.quantity
		);
	}
	getVenue(forcedId: string | null = null): Promise<Venue> {
		let venueId = forcedId;
		if (venueId === null) {
			venueId = this.getStoredVenueId();
		}
		if (venueId === null || venueId.length <= 0) {
			if (this._venue) {
				return new Promise<Venue>(resolve => resolve(this._venue));
			} else {
				return new Promise<Venue>((resolve, reject) => reject('No Venue ID Provided'));
			}
		}
		if (this._venue && venueId === this._venue._id) {
			this.venue.emit(this._venue);
			return new Promise<Venue>(resolve => resolve(this._venue));
		}
		if (this.venuePromiseRunning === venueId) {
			return this.venuePromise;
		}
		this.venuePromiseRunning = venueId;
		this.venuePromise = new Promise<Venue>(async (resolve, reject) => {
			try {
				const venue = (await Api.getLazyVenue(venueId)).data;
				const venueTagsMap = new Map((venue.tags || []).map(t => [t._id, t]));

				const resultMappingOpeningHours = TimeUtils.sanitizeHours(venue.openingHours);
				venue.openingHours = TimeUtils.checkSpecialHours(resultMappingOpeningHours, venue?.specialOpeningHours ?? []);

				// venue.deliveryHours = TimeUtils.checkSpecialHours(resultMappingDeliveryHours, venue?.specialDeliveryHours ?? []);
				// venue.deliveryHours = TimeUtils.checkSpecialHoursForDelivery(
				// 	mappindDelivery,
				// 	venue?.specialOpeningHours ?? [],
				// 	venue?.specialDeliveryHours ?? []
				// );
				const resultMappingDeliveryHours = TimeUtils.sanitizeHours(venue.deliveryHours);

				const data = TimeUtils.checkSpecialHours(resultMappingDeliveryHours, venue?.specialDeliveryHours ?? []);
				venue.deliveryHours = TimeUtils.checkSpecialHoursForDelivery(data, venue?.specialOpeningHours ?? []);

				const [articleCategoriesResponse, optionGroupsResponse] = await Promise.all([
					Api.getCategoriesWithArticles(venue._id, true),
					Api.getOptionGroupsByVenue(venue._id),
				]);
				venue.articleCategories = articleCategoriesResponse.data;
				venue.panicEndAt = venue.panicEndAt && moment(venue.panicEndAt);
				venue.panicEndAt = venue.panicEndAt || moment().add(-1, 'day');
				venue.tips = venue.tips.map(tip => ({
					...tip,
					defaultTips: tip.defaultTips.map(defaultTip => ({
						...defaultTip,
						value: numberD(defaultTip.value),
					})),
				}));
				const optionGroupsById = this.createOptionGroupsMap(optionGroupsResponse.data, venueTagsMap);
				venue.articleCategories = venue.articleCategories
					.map(category => {
						// filter inactive categories
						category.articles = category.articles
							.map(article => {
								// filter inactive groups (empty options)
								article.groups = article.groups
									.map(groupId => {
										const optionGroupId = this.getOptionGroupId(groupId as string | OptionGroup);
										return optionGroupId ? optionGroupsById.get(optionGroupId) ?? null : null;
									})
									.filter(it => !!it)
									.sort((a, b) => numberComparator(a.sortOrder, b.sortOrder))
									.filter(group => group.articles.length > 0);
								article = this.sanitizeArticle(article, venueTagsMap);
								return article;
							})
							.sort((a, b) => numberComparator(a.sortOrder, b.sortOrder));
						return category;
					})
					.map(category => {
						if (!category?.sanitizedHours) {
							category.availableHours = TimeUtils.sanitizeHours(category.availableHours);
						}
						category.hidden = !(
							category.visible &&
							category.articles.length > 0 &&
							(!category.availableHours ||
								category.availableHours.length === 0 ||
								TimeUtils.doesHoursMatchNow(category.availableHours))
						);
						return category;
					})
					.sort((a, b) => numberComparator(a.sortOrder, b.sortOrder));
				this._venue = venue;
				this.analytics.setVenue(venue?.name ?? '');
				this.analytics.setCountry(venue?.country ?? '');
				this.venue.emit(this._venue);
				resolve(this._venue);
			} catch (e) {
				this.venue.emit(null);
				this.venue.error(e);
				reject(e);
			}
		}).finally(() => {
			this.venuePromiseRunning = null;
		});
		return this.venuePromise;
	}

	private createOptionGroupsMap(optionGroups: OptionGroup[], venueTagsMap: Map<string, any>): Map<string, OptionGroup> {
		return optionGroups.reduce((groupsMap, group) => {
			if (!group?._id) {
				return groupsMap;
			}

				group.displayMode = group.displayModeMobile ?? group.displayMode;
				if (group.displayMode === DisplayMode.DEFAULT) {
					group.displayMode = DisplayMode.CUSTOM;
				}
				group.articles = (group.articles || [])
					.map(optionArticle => this.sanitizeArticle(optionArticle, venueTagsMap))
					.sort((a, b) => numberComparator(a.sortOrder, b.sortOrder));
				group.tags = this.resolveVenueTags(group.tags, venueTagsMap);
				groupsMap.set(group._id, group);
				return groupsMap;
			}, new Map<string, OptionGroup>());
	}

	private getOptionGroupId(group: string | OptionGroup): string | null {
		if (!group) {
			return null;
		}
		if (typeof group === 'string') {
			return group;
		}
		return group._id ?? null;
	}

	async getTable(venueId: string, tableNumber: string): Promise<Table> {
		return (await Api.getTable(venueId, tableNumber)).data;
	}
	private startTimer() {
		this.sanifairSub = timer(0, 3 * 60000).subscribe(timerValue => {
			if (timerValue) {
				console.log(timerValue);
				this.extendOrderWithSanifair(this._verifiedOrder);
			}
		});
	}
	private stopTimer() {
		if (this.sanifairSub) this.sanifairSub.unsubscribe();
	}
	createOrder(venue: Venue, address: Address, orderType: OrderType = OrderType.PREORDER, preorderType: PreorderType, table?: Table) {
		console.log('createOrder', table);
		Sentry.captureMessage(`generating Order for ${venue.readableId}`);
		const order = new Order();
		order.orderedArticles = [];
		order._id = undefined;
		order.venue = venue._id;
		order.currency = venue.currency;
		order.type = orderType;
		if (order.type === OrderType.PREORDER) {
			order.preorder = new Preorder();
			order.preorder.number = '';
			order.preorder.type = preorderType;
			order.preorder.personCount = 1;
			order.table = venue.preorderTable;
			order.tableNumber = 'preorder_table';
			if (preorderType === PreorderType.TAKE_AWAY || preorderType === PreorderType.INSIDE) {
				order.preorder.street = venue.street;
				order.preorder.city = venue.city.de;
				order.preorder.number = venue.number;
				order.preorder.postalCode = venue.postalCode;
				order.preorder.country = venue.country;
				order.preorder.lat = venue.location.coordinates[1];
				order.preorder.lng = venue.location.coordinates[0];
			} else if (address) {
				order.preorder.street = address.street;
				order.preorder.city = address.city;
				order.preorder.number = address.number;
				order.preorder.postalCode = address.postalCode;
				order.preorder.country = address.country;
				order.preorder.lat = address.lat;
				order.preorder.lng = address.lng;
			}
		} else if (order.type === OrderType.CATERING) {
			order.preorder = new Preorder();
			order.preorder.street = venue.street;
			order.preorder.city = venue.city.de;
			order.preorder.number = venue.number;
			order.preorder.postalCode = venue.postalCode;
			order.preorder.country = venue.country;
			order.preorder.lat = venue.location.coordinates[1];
			order.preorder.lng = venue.location.coordinates[0];
		} else if (order.type === OrderType.STANDARD) {
			order.table = table?._id;
			order.tableNumber = table?.number;
			order.preorder = new Preorder();
		}
		this.address.emit(address);
		this.order.next(order);
	}

	async checkOpenPayment(): Promise<{ payment: Payment; order: Order }> {
		console.log('checkOpenPayment()');

		const verifiedOrder = this._verifiedOrder;
		if (verifiedOrder && verifiedOrder.isPayed) {
			console.log(`VerifiedOrder ${verifiedOrder.code} is paid`);
			return;
		}
		try {
			const paymentIdToCheck = verifiedOrder?.paymentId || this._payment?._id;
			if (!paymentIdToCheck) {
				console.log('No PaymentId to check');
				return null;
			}
			console.log({
				paymentIdFromVerifiedOrder: verifiedOrder?.paymentId,
				globalPaymentId: this._payment?._id,
			});
			this.compressedStorageService.setItem('paymentId', null);
			const checkingPaymentModal = await this.showCheckingPaymentModal();
			let dialogIsOpen = true;
			const requirePaymentDone = this.reloadPaymentUntilDoneOrInactive(paymentIdToCheck, 1500, () => dialogIsOpen);
			checkingPaymentModal.onDidDismiss().then(() => (dialogIsOpen = false));
			const payment = await requirePaymentDone;
			if (dialogIsOpen) {
				await checkingPaymentModal.dismiss();
			}
			const order = (await Api.getOrder(payment.orders[0])).data;
			return {
				payment,
				order,
			};
		} catch (e) {
			this.compressedStorageService.setItem('paymentId', null);
			console.log(e);
		}
		return null;
	}

	private async reloadPaymentUntilDoneOrInactive(paymentId: string, sleepMs: number = 1500, active: () => boolean): Promise<Payment> {
		let lastPayment: Payment;
		do {
			lastPayment = (await Api.getPayment(paymentId)).data;
			console.log(lastPayment.status);
			if (lastPayment.status === PaymentStatus.done) {
				return lastPayment;
			}
			await sleep(sleepMs);
		} while (active());
		return lastPayment;
	}
	private async showCheckingPaymentModal(): Promise<HTMLIonModalElement> {
		return CheckingPaymentModalComponent.show(this.modalCtrl);
	}

	async sendOrderAndPay(
		modalCtrl: ModalController,
		order: Order,
		tip: number,
		authToken: string,
		customer: Customer,
		keepPreorder = false,
		forceAddressInput = false,
		withoutPayment = false,
		company: Company = null
	): Promise<void> {
		order.userUid = customer.uid;

		const orderCopy: Order = JSON.parse(JSON.stringify(order));

		if (keepPreorder) {
			orderCopy.type = OrderType.STANDARD;
		}
		if (authToken) {
			const testOrderRes = await Api.createTestOrder(authToken, orderCopy);
			const testPayment = new Payment();
			testPayment._id = testOrderRes.data.payment;
			this.payment.emit(testPayment);
			this.verifiedOrder.emit(testOrderRes.data.order);
			return;
		}
		this.analytics.orderCreationAttempted(order);
		let orderResponse: Order;
		try {
			if (order._id) {
				//TODO: request update order
				orderResponse = (await Api.updateOrder(orderCopy))?.data;
			} else {
				orderResponse = (await Api.createOrder(orderCopy, keepPreorder))?.data;
			}
		} catch (creationError) {
			const { error_type, ...extraParams } = this.classifyOrderCreationError(creationError);
			this.analytics.orderCreationFailed(order, error_type, extraParams);
			throw creationError;
		}
		Sentry.captureMessage('Order Verified');
		this.analytics.orderVerified(order);
		const venue = await this.getVenue();
		const verifiedOrder = orderResponse;
		this.verifiedOrder.emit(verifiedOrder);
		console.log('company', company);
		if (
			Number(verifiedOrder.totalPrice) === 0 ||
			(company && company?.isPaidByCompany) ||
			(company && company.subsidy && numberD(company.subsidy) >= Number(verifiedOrder.totalPrice))
		) {
			const payment = paymentFromOrders([verifiedOrder], tip);
			payment.method = PaymentMethod.ZERO_PAYMENT;
			const createdPayment = await Api.createPayment(payment);
			this.verifiedOrder.emit(verifiedOrder);
			this.payment.emit(createdPayment);
			return;
		}
		if (venue.worldlineEnabled && environment.allowNavigationToWorldline) {
			const newPayment = paymentFromOrder(verifiedOrder, tip);
			newPayment.method = PaymentMethod.WORLDLINE;
			const payment = await Api.createWorldLinePayment(paymentBody(newPayment), verifiedOrder.venue);
			if (payment?.worldlinePayment?.paymentPage) {
				// window.open(payment?.worldlinePayment?.paymentPage, '_blank');
				window.location.assign(payment?.worldlinePayment?.paymentPage);
				this.payment.emit(payment);
				return;
			} else {
				throw new Error('Problems with creating payment...');
			}
		}
		if (withoutPayment) {
			return;
		}

		const result = await this.showPaymentModal(modalCtrl, venue, verifiedOrder, customer, tip, forceAddressInput);
		if (result?.result === PaymentModalResult.SUCCESS) {
			this.verifiedOrder.emit(verifiedOrder);
			this.payment.emit(result.payment);
			return;
		}
		throw result?.error ?? null;
	}

	private classifyOrderCreationError(
		error: any
	): { error_type: string; http_status?: number; error_code?: number | string; error_message?: string } {
		if (!error) {
			return { error_type: 'unknown' };
		}

		const status: number | undefined = error?.status ?? error?.response?.status;
		const code: number | string | undefined = error?.error?.code ?? error?.response?.data?.code ?? error?.code;
		const message: string | undefined = error?.error?.message ?? error?.response?.data?.message ?? error?.message;

		if (!status) {
			return {
				error_type: 'network_error',
				...(code != null ? { error_code: code } : {}),
				...(message ? { error_message: message } : {}),
			};
		}

		if (status === 400) {
			return {
				error_type: 'validation_error',
				http_status: status,
				...(code != null ? { error_code: code } : {}),
				...(message ? { error_message: message } : {}),
			};
		}

		if (status === 409) {
			return {
				error_type: 'conflict_error',
				http_status: status,
				...(code != null ? { error_code: code } : {}),
				...(message ? { error_message: message } : {}),
			};
		}

		if (status >= 500) {
			return {
				error_type: 'server_error',
				http_status: status,
				...(code != null ? { error_code: code } : {}),
				...(message ? { error_message: message } : {}),
			};
		}

		return {
			error_type: 'unknown',
			http_status: status,
			...(code != null ? { error_code: code } : {}),
			...(message ? { error_message: message } : {}),
		};
	}

	async showPaymentModal(
		modalCtrl: ModalController,
		venue: Venue,
		verifiedOrder: Order,
		customer: Customer,
		tip: number,
		forceAddressInput: boolean
	): Promise<{ result: PaymentModalResult; error: any; payment: Payment }> {
		console.log('showPaymentModal()');
		const result = await PaymentModalComponent.show(modalCtrl, venue, [verifiedOrder], customer, tip, forceAddressInput, payment => {
			this.payment.emit(payment);
			console.log(payment);
		});
		console.log(result);
		try {
			await modalCtrl.dismiss();
		} catch (e) {
			console.log('Ignore dismiss modal error');
		}
		if (result?.result === PaymentModalResult.SUCCESS) {
			console.log('showPaymentModal(). SUCCESS');
			return result;
		}
		if (result.result === PaymentModalResult.ERROR) {
			console.log('showPaymentModal(). ERROR');
			const retryResponse = await PaymentCancelledModalComponent.show(modalCtrl);
			console.log({ retryResponse });
			if (retryResponse) {
				return await this.showPaymentModal(modalCtrl, venue, verifiedOrder, customer, tip, forceAddressInput);
			}
			console.log('User does not RETRY: Finally failed.');
			return result;
		}
	}

	getStoredVenueId(): string {
		return this._order ? this._order.venue : null;
	}

	async reorder(translate: TranslateService, order: Order) {
		const anyOrder = JSON.parse(JSON.stringify(order));
		anyOrder.totalPrice = undefined;
		anyOrder.orderedArticles = anyOrder.orderedArticles.map(oa => {
			oa.totalPrice = undefined;
			return oa;
		});
		const localOrder: Order = anyOrder;
		localOrder.status = undefined;
		const venue = await this.getVenue(localOrder.venue);
		if (
			OrderUtils.isDelivery(localOrder) &&
			venue.deliveryEnabled &&
			venue.deliveryPostalCodes &&
			venue.deliveryPostalCodes.length > 0
		) {
			venue.isPostalDelivery = true;
		}
		if (!venueAcceptsOrders(venue, localOrder.preorder.type)) {
			throw {
				message: translate.instant('errors.reorder_venue_not_open'),
			};
		}
		const articleList = [];
		for (const category of venue.articleCategories) {
			for (const article of category.articles) {
				articleList.push(article);
				for (const group of article.groups) {
					for (const optionArticle of group.articles) {
						articleList.push(optionArticle);
					}
				}
			}
		}
		localOrder.orderedArticles = localOrder.orderedArticles
			.filter(it => !it.isPromo || !it.freeArticle)

			.map(oa => {
				oa.article = articleList.find(
					article => oa.article._id === article._id && isActive(articleList, article, localOrder.type, localOrder.preorder.type)
				);
				if (!oa.article) {
					return null;
				}
				oa.groups = oa.groups
					.map(ao => {
						ao.article = articleList.find(
							article =>
								ao.article._id === article._id && isActive(articleList, article, localOrder.type, localOrder.preorder.type)
						);
						if (!ao.article) {
							return null;
						}
						return ao;
					})
					.filter(ao => !!ao);
				return oa;
			})
			.filter(oa => !!oa);
		localOrder.orderAt = null;
		localOrder._id = null;
		if (localOrder.promoCode) {
			this.order.next(OrderUtils.removePromo(localOrder));
		} else {
			this.order.next(localOrder);
		}
	}

	async getAllVenues(customerGroup: string): Promise<Venue[]> {
		return (await Api.getAllVenues(customerGroup)).data.map(ven => {
			try {
				ven.panicEndAt = ven.panicEndAt && moment(ven.panicEndAt);
				// if no panic set to "yesterday" to keep non-null and as "Moment"
				ven.panicEndAt = ven.panicEndAt || moment().add(-1, 'day');
				ven.openingHours = TimeUtils.sanitizeHours(ven.openingHours, ven?.specialOpeningHours ?? []);
				ven.deliveryHours = TimeUtils.sanitizeHours(ven.deliveryHours);
			} catch (e) {
				console.error({
					message: 'Error while sanitizing hours ' + e,
					venue: ven.name + ' ' + ven.readableId,
					openingHours: ven.openingHours,
					deliveryHours: ven.deliveryHours,
				});
			}
			return ven;
		});
	}

	async getVenuesByAddress(customerGroup: string, address: Address): Promise<Venue[]> {
		const venues: Venue[] = [];
		if (address.postalCode) {
			venues.push(
				...(await this.getVenues(customerGroup, null, null, address.postalCode))
					.map(ven => {
						ven.isPostalDelivery = true;
						return ven;
					})
					.filter(ven => {
						return venueAcceptsOrders(ven, PreorderType.DELIVERY);
					})
			);
		}
		const ids = venues.map(ven => ven._id);
		(await this.getVenues(customerGroup, address.lat, address.lng, null))
			.filter(ven => {
				return venueAcceptsOrders(ven, PreorderType.DELIVERY) || venueAcceptsOrders(ven, PreorderType.TAKE_AWAY);
			})
			.forEach(ven => {
				if (ids.indexOf(ven._id) < 0) {
					venues.push(ven);
				}
			});
		return venues;
	}
	async invalidateOrder(order: Order) {
		// try {
		// 	if (order?._id) {
		// 		const result = await Api.invalidateOrder(order?._id);
		// 		order.sanifairVouchers = [];
		// 	}
		// } catch (e) {
		// 	console.log('Error while updating');
		// }
	}
	async extendOrderWithSanifair(order: Order) {
		this.stopTimer();
		try {
			if (order?._id && order.status === OrderStatus.CREATED) {
				await Api.extendSanifairVoucher(order?._id);
				this.startTimer();
			}
			return order;
		} catch (e) {
			this.startTimer();
		}
	}
	async getVenues(customerGroup: string, lat: number | null, lng: number | null, postalCode: string | null): Promise<Venue[]> {
		const response = await (postalCode
			? Api.getVenuesBy(postalCode, customerGroup)
			: Api.getVenues(customerGroup, lat, lng, 50, false));
		return response.data
			.map(venue => {
				venue.openingHours = TimeUtils.sanitizeHours(venue.openingHours);
				venue.deliveryHours = TimeUtils.sanitizeHours(venue.deliveryHours);
				venue.isPostalDelivery = !!postalCode;
				if (venue.deliveryByRadius === undefined) {
					venue.deliveryByRadius = false;
				}
				return venue;
			})
			.filter(async venue => {
				try {
					if (venue.panicEndAt && moment(venue.panicEndAt).isSameOrAfter(moment())) {
						return false;
					}
					return (
						(await getSlots(venue, OrderType.PREORDER, PreorderType.DELIVERY)).length !== 0 ||
						(await getSlots(venue, OrderType.PREORDER, PreorderType.TAKE_AWAY)).length !== 0
					);
				} finally {
				}
				return false;
			});
	}

	makeId(length: number): string {
		let result = '';
		const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		const charactersLength = characters.length;
		for (let i = 0; i < length; i++) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength));
		}
		return result;
	}

	async sendOrderStandard(modalCtrl: ModalController, order: Order, customer: Customer, keepPreorder: boolean = false): Promise<Order> {
		order.userUid = customer.uid;
		order.orderAt = moment().add(1, 'minute');
		return new Promise<Order>(async (resolve, reject) => {
			try {
				const orderCopy: Order = JSON.parse(JSON.stringify(order));
				const response = await Api.createOrder(orderCopy, keepPreorder);
				const verifiedOrder = response.data;
				this.analytics.orderVerified(verifiedOrder);
				resolve(verifiedOrder);
				const table = (await Api.getTable(orderCopy.venue, orderCopy.tableNumber)).data;
				this.createOrder(this._venue, this._address, OrderType.STANDARD, PreorderType.INSIDE, table);
				delay(100);
				this._order.table = table._id;
				this._order.tableNumber = table.number;
				this.order.next(this._order);
			} catch (e) {
				reject(e);
			}
		});
	}

	private sanitizeArticle(article: Article, venueTagsMap: Map<string, any>): Article {
		if (!article) {
			return article;
		}
		if (article.groups) {
			article.isFromPrice = article.groups.some(g => g.displayIdentifiers?.includes(DisplayIdentifier.fullPrice));
		}
		if (!article.sanitizedHours) {
			article.availableHours = TimeUtils.sanitizeHours(article.availableHours);
			article.sanitizedHours = true;
		}

		const hasAvailableHours = article.availableHours?.length > 0;
		const isAvailableNow = !hasAvailableHours || TimeUtils.doesHoursMatchNow(article.availableHours);
		article.hidden = !article.visible || !isAvailableNow;

		if (article.displayPrice != null) {
			article.price = article.displayPrice;
		}

		article.tags = this.resolveVenueTags(article.tags, venueTagsMap);
		return article;
	}

	private resolveVenueTags(tags: any[], venueTagsMap: Map<string, any>): any[] {
		return (tags || [])
			.map(tag => {
				if (typeof tag === 'string') {
					return venueTagsMap.get(tag);
				}
				if (tag?._id === undefined) {
					return venueTagsMap.get(tag);
				}
				return tag;
			})
			.filter(tag => tag != null);
	}
	async cleanRepository() {
		this.analytics.setVenue('');
		this.analytics.setCountry('');
		this.resetVenue();
		this.address.emit(null);
		this.order.next(null);
		this.verifiedOrder.emit(null);
		this.payment.emit(null);
	}
	async sendOrderWithoutNavToPayment(
		modalCtrl: ModalController,
		translate: TranslateService,
		order: Order,
		tip: number,
		loading: (loading: boolean) => void,
		snackbarCtrl: MatSnackBar,
		keepPreorder = false,
		forceAddressInput = false
	) {
		this._order._id = undefined;
		loading(true);
		try {
			if (this._venue.checkoutIntegration && this._venue.checkoutIntegration === CheckoutIntegrationType.SIMPHONY) {
				// order.tableNumber = environment.tableNumberSimphony;
				// order.table = environment.tableIDSimohony;
			}
			if (!order.table) {
				order.table = this._venue.preorderTable;
			}

			this._order.status = OrderStatus.CREATED;

			await this.sendOrderAndPay(
				modalCtrl,
				order,
				tip,
				this._authLogin ? this._authLogin.token : null,
				this._customerAuth.customer,
				keepPreorder,
				forceAddressInput,
				true
			);
		} catch (e) {
			loading(false);

			return { error: 'cant create order' };
		}
		loading(false);
	}
	async createOrderWithPayment(
		modalCtrl: ModalController,
		translate: TranslateService,
		order: Order,
		tip: number,
		loading: (loading: boolean) => void,
		snackbarCtrl: MatSnackBar,
		keepPreorder = false,
		forceAddressInput = false,
		company: Company = null
	) {
		loading(true);
		try {
			if (this._venue.checkoutIntegration && this._venue.checkoutIntegration === CheckoutIntegrationType.SIMPHONY) {
				// order.tableNumber = environment.tableNumberSimphony;
				// order.table = environment.tableIDSimohony;
			}
			if (!order.table) {
				order.table = this._venue.preorderTable;
			}
			if (order.status) order.status = OrderStatus.CREATED;

			if (order.promoCode && order.promoCode.code === 'DND_HALF_DISCOUNT') {
				if (!order.orderedArticles.some(ag => order.promoCode.value.articles.includes(ag.article._id))) {
					delete order.promoCode;
				}
			}

			await this.sendOrderAndPay(
				modalCtrl,
				order,
				tip,
				this._authLogin ? this._authLogin.token : null,
				this._customerAuth.customer,
				keepPreorder,
				forceAddressInput,
				false,
				company
			);
			setTimeout(async () => {
				try {
					await modalCtrl.dismiss();
				} catch (e) {}
				await this.navService.paymentSuccess(this._verifiedOrder._id, this._payment._id);
			}, 1000);
		} catch (err) {
			if (err === null) {
				loading(false);

				return;
			}
			if (order && order._id && order?.sanifairVouchers) {
				this.invalidateOrder(order);
				this.verifiedOrder.emit(null);
			}
			if (err.error === PaymentMethod.PAYPAL) {
				await this.navService.paymentError(err.orderId, '');
				loading(false);
			}
			if (err?.error != PaymentMethod.PAYPAL) {
				const message = axiosErrorToMessage(translate, err, order.orderAt);
				console.error(message);
				snackbarCtrl.open(message, null, {
					duration: 5000,
				});
			}
		}
		loading(false);
	}
	addFavourite(articleNumber: string) {
		if (articleNumber) {
			let favouriteArticles = this._favorite;
			if (favouriteArticles && favouriteArticles.length > 0) {
				const foundArticle = favouriteArticles.includes(articleNumber);
				if (!foundArticle) {
					favouriteArticles.push(articleNumber);
					this.favorite.emit(favouriteArticles);
				}
			} else {
				favouriteArticles = [];
				favouriteArticles.push(articleNumber);
				this.favorite.emit(favouriteArticles);
			}
		}
	}
	checkIfArticleIsFavourite(articleNumber: string) {
		return this._favorite && this._favorite.length > 0 ? this._favorite.includes(articleNumber) : false;
	}
	deleteFavourite(articleNumber: string) {
		if (articleNumber && this._favorite && this._favorite.length > 0 && this._favorite.includes(articleNumber)) {
			const deletedArticle = this._favorite.filter(it => it !== articleNumber);
			this.favorite.emit(deletedArticle);
		}
	}
	async setupCompanyCode(companyData: Company) {
		this.companyCode$.next(companyData);
	}
}
